import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlay, faStop, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import styles from './DailyPlan.module.css';
import moment from 'moment';
import axios from 'axios';
import { fetchGoals } from '../../apis/dailyPlanApi'; // 调整路径到你的api.js所在的正确位置

const TaskList = ({ dailyPlan, allGoals, handleRemoveTask, handleNoteChange }) => {
    const [isTiming, setIsTiming] = useState({});
    const [formattedTime, setFormattedTime] = useState({});
    const intervalRefs = useRef({});
    const startTimeRefs = useRef({});

    useEffect(() => {
        if (dailyPlan && dailyPlan.selectedTasks) {
            dailyPlan.selectedTasks.forEach(({ task }) => {
                if (task) {
                    const savedTimingState = localStorage.getItem(`isTiming-${task._id}`);
                    const savedStartTime = localStorage.getItem(`startTime-${task._id}`);

                    if (savedTimingState === 'true' && savedStartTime) {
                        setIsTiming(prev => ({ ...prev, [task._id]: true }));
                        startTimeRefs.current[task._id] = moment(savedStartTime);
                        startTimingTask(task._id, true);
                    }
                }
            });
        }
    }, [dailyPlan]);

    const handleEditTask = async (task, goalId, field, value) => {
        if (!task) return; // 防御性检查
        const updatedTask = { ...task, [field]: value };
        try {
            console.log(`更新任务: ${goalId}/tasks/${task._id}`);
            await axios.put(`/goals/${goalId}/tasks/${task._id}`, updatedTask);

            const goalResponse = await axios.get(`/goals/${goalId}`);
            const updatedGoalExpectedTime = goalResponse.data.tasks.reduce((total, t) => total + (t.expectedTime || 0), 0);
            await axios.put(`/goals/${goalId}`, { ...goalResponse.data, expectedTime: updatedGoalExpectedTime });

            fetchGoals();
        } catch (error) {
            console.error('更新任务时出错:', error);
        }
    };

    const toggleTaskComplete = async (task, goalId) => {
        if (!task) return; // 防御性检查
        const updatedTask = { ...task, isComplete: !task.isComplete };
        try {
            await axios.put(`/goals/${goalId}/tasks/${task._id}`, updatedTask);
            fetchGoals();
        } catch (error) {
            console.error('切换任务完成状态时出错:', error);
        }
    };

    const startTimingTask = (taskId, isInitialLoad = false) => {
        if (!isInitialLoad) {
            startTimeRefs.current[taskId] = moment();
        }
        setIsTiming(prev => ({ ...prev, [taskId]: true }));
        intervalRefs.current[taskId] = setInterval(() => {
            const elapsedTime = moment().diff(startTimeRefs.current[taskId], 'seconds');
            const minutes = Math.floor(elapsedTime / 60);
            const seconds = elapsedTime % 60;
            setFormattedTime(prev => ({
                ...prev,
                [taskId]: `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
            }));
        }, 1000);
    };

    const stopTimingTask = async (task, goalId) => {
        if (!task) return; // 防御性检查
        clearInterval(intervalRefs.current[task._id]);
        setIsTiming(prev => ({ ...prev, [task._id]: false }));

        const endTime = moment();
        const duration = endTime.diff(startTimeRefs.current[task._id], 'minutes');

        try {
            const goalResponse = await axios.get(`/goals/${goalId}`);
            const taskFromServer = goalResponse.data.tasks.find(t => t._id === task._id);
            const currentInvestedTime = taskFromServer.investedTime;

            const updatedInvestedTime = currentInvestedTime + duration;

            console.log(`更新前 - 任务ID: ${task._id}, 目标ID: ${goalId}, 当前投入时间: ${currentInvestedTime}`);
            console.log(`计时持续时间: ${duration}`);
            console.log(`更新后投入时间: ${updatedInvestedTime}`);

            const updatedTask = { ...task, investedTime: updatedInvestedTime };
            await axios.put(`/goals/${goalId}/tasks/${task._id}`, updatedTask);

            const updatedGoalInvestedTime = goalResponse.data.tasks.reduce((total, t) => {
                return t._id === task._id ? total + updatedInvestedTime : total + t.investedTime;
            }, 0);
            await axios.put(`/goals/${goalId}`, { ...goalResponse.data, investedTime: updatedGoalInvestedTime });

            fetchGoals();

            const newTaskRecord = {
                task: task.task,
                category: task.category,
                duration: duration,
                expectedTime: task.expectedTime,
                startTime: startTimeRefs.current[task._id].toISOString(),
                endTime: endTime.toISOString(),
                investedTime: updatedInvestedTime,
                isComplete: task.isComplete,
                weight: task.weight
            };
            await axios.post('/tasks', newTaskRecord);
        } catch (error) {
            console.error('更新任务时长时出错:', error);
        }
    };

    const getProgress = (investedTime, expectedTime) => {
        if (!expectedTime) return 0;
        return Math.min((investedTime / expectedTime) * 100, 100);
    };

    return (
        <div className={styles.taskList}>
            {dailyPlan && dailyPlan.selectedTasks ? (
                dailyPlan.selectedTasks.map(({ goalId, taskId, task }) => {
                    if (!task) {
                        console.warn(`任务ID ${taskId} 在目标ID ${goalId} 中未找到任务详情`);
                        return null; // 跳过
                    }

                    return (
                        <div key={`${goalId}-${taskId}`} className={styles.taskDetails}>
                            <div className={styles.taskInfo}>
                                <div className={styles.taskHeader}>
                                    <p>
                                        <strong>所属目标:</strong>
                                        <Link to={`/goals?goalId=${goalId}`}>
                                            {allGoals.find(goal => goal._id === goalId)?.name || '目标未找到'}
                                        </Link>
                                    </p>
                                    <p><strong>任务类别:</strong> {task.category || 'N/A'}</p>
                                    <p><strong>任务权重:</strong> {task.weight || 'N/A'}</p>
                                </div>
                                <h5 className={styles.taskName}>{task.task || '任务未找到'}</h5>
                                <p><strong>预计投入时间:</strong> {task.expectedTime || 'N/A'} 分钟</p>
                                <p><strong>累计投入时间:</strong> {task.investedTime || 'N/A'} 分钟</p>
                                <Form.Group className="mb-4">
                                    <Form.Label><strong>任务备注:</strong></Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        defaultValue={task.notes || ''}
                                        onBlur={(e) => handleNoteChange(goalId, taskId, e.target.value)}
                                    />
                                </Form.Group>
                                <ProgressBar
                                    now={getProgress(task.investedTime, task.expectedTime)}
                                    label={`${getProgress(task.investedTime, task.expectedTime).toFixed(2)}%`}
                                />
                            </div>
                            <div className="task-actions">
                                {isTiming[task._id] && (
                                    <span className="me-2">
                                    {formattedTime[task._id]}
                                </span>
                                )}
                                <Button
                                    className="btn btn-sm me-2"
                                    onClick={() => toggleTaskComplete(task, goalId)}
                                >
                                    <FontAwesomeIcon icon={task.isComplete ? faCheckSquare : faSquare} />
                                </Button>
                                <Button
                                    className="btn btn-primary btn-sm me-2"
                                    onClick={() => isTiming[task._id] ? stopTimingTask(task, goalId) : startTimingTask(task._id)}
                                >
                                    <FontAwesomeIcon icon={isTiming[task._id] ? faStop : faPlay} />
                                </Button>
                                <Button className="btn btn-danger btn-sm" onClick={() => handleRemoveTask(goalId, taskId)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </div>
                        </div>
                    );
                })
            ) : (
                <p>暂无任务</p>
            )}
        </div>
    );
};

export default TaskList;
